// LES TRES ANÀLISIS DEL SSM (Checkland, «finding out»).
//
// Fora de React i sense cap import. Es proven soles: `npm run prova:analisis`.
//
// ── PER QUÈ HI SÓN ───────────────────────────────────────────────────────────────────────────
//
// L'etapa 1 tenia una descripció, una llista de qui hi és i una de preocupacions. Això és una
// pluja d'idees ordenada, i **el que distingeix el SSM d'una pluja d'idees és justament això**:
// tres maneres estructurades de mirar-se la situació abans de decidir res.
//
// I hi ha un motiu pràctic, a més del metodològic: **l'etapa 6 té dos eixos** —sistèmicament
// desitjable i culturalment factible— i el segon **no tenia cap font a l'aplicació**. Sense les
// Anàlisis 2 i 3, «culturalment factible» és una casella on escriure el que un s'imagini, que és
// exactament el que Checkland volia evitar.
//
// ── ANÀLISI 1: LA INTERVENCIÓ ────────────────────────────────────────────────────────────────
//
//     client        qui ha fet que aquest estudi passi
//     qui l'estudia qui el fa (normalment, qui està mirant la pantalla)
//     AMOS DEL      aquells les preocupacions dels quals defineixen QUIN és el problema
//     PROBLEMA
//
// **Són papers i no persones**: una mateixa persona en pot fer diversos, i el mateix paper el
// poden fer molts. I la tercera fila **és una decisió, no una troballa**: qui l'estudia tria a
// qui té per amo del problema, i **triar-ne uns altres dona un altre estudi**. És el punt que
// més costa d'entendre i el que fa que el SSM no sigui neutre.
//
// ── ANÀLISI 2: EL SISTEMA SOCIAL ─────────────────────────────────────────────────────────────
//
//     paper   una posició social, formal o no: «cap de torn», «el que tothom pregunta»
//     normes  la conducta que s'espera de qui fa aquell paper
//     valors  amb què es jutja si el fa bé
//
// Les tres es redefineixen contínuament l'una a l'altra, i per això van juntes a la mateixa fila
// i no a tres llistes.
//
// ── ANÀLISI 3: EL SISTEMA POLÍTIC ────────────────────────────────────────────────────────────
//
// **Com s'expressa el poder.** No «qui mana», que és la resposta fàcil i sol ser falsa, sinó
// **què és aquí una moneda de poder** i com s'obté, es fa servir, es guarda i es passa. L'autoritat
// formal n'és una; també ho són qui té la informació, qui és a quina comissió, i a qui es pot
// trucar.

export const PAPERS_INTERVENCIO = ['client', 'practitioner', 'issueOwners'];

// Monedes de poder que val la pena tenir al davant. **No és una llista tancada ni una plantilla
// per omplir**: són exemples per no quedar-se només amb l'autoritat formal, que és la que es veu
// i gairebé mai la que decideix.
export const MONEDES = ['formalAuthority', 'expertise', 'information', 'access', 'reputation', 'resources'];

export const analisisBuides = () => ({
  analysisOne: { client: [], practitioner: [], issueOwners: [] },
  analysisTwo: [],
  analysisThree: [],
});

const net = (v) => String(v || '').trim();
const teAlguna = (llista) => (llista || []).some((x) => net(x));

// ── QUÈ HI HA FET ────────────────────────────────────────────────────────────────────────────

export function feta(problemSituation, quina) {
  const p = problemSituation || {};
  if (quina === 1) {
    const a = p.analysisOne || {};
    return PAPERS_INTERVENCIO.some((k) => teAlguna(a[k]));
  }
  if (quina === 2) return (p.analysisTwo || []).some((f) => net(f.role));
  return (p.analysisThree || []).some((f) => net(f.commodity) || net(f.how));
}

export const capFeta = (p) => ![1, 2, 3].some((q) => feta(p, q));

// ── EL QUE ALIMENTA L'EIX «CULTURALMENT FACTIBLE» ────────────────────────────────────────────
//
// L'etapa 6 ha de decidir si un canvi passaria de debò en aquesta cultura. La pregunta no és
// «és bona idea?» —això és l'altre eix— sinó **qui hi hauria de canviar de conducta, i qui té
// prou poder per aturar-ho**. Les dues respostes surten d'aquí, i per això aquesta funció existeix
// abans que l'etapa 6: sense una font, aquell eix seria una casella on escriure el que un
// s'imagini.
export function contextCultural(problemSituation) {
  const p = problemSituation || {};
  return {
    // Els papers de l'Anàlisi 2: qui hauria de fer les coses d'una altra manera.
    papers: (p.analysisTwo || []).filter((f) => net(f.role))
      .map((f) => ({ id: f.id, nom: net(f.role), normes: net(f.norms), valors: net(f.values) })),
    // Les monedes de l'Anàlisi 3: què hi ha en joc per a qui podria dir que no.
    poder: (p.analysisThree || []).filter((f) => net(f.commodity) || net(f.how))
      .map((f) => ({ id: f.id, moneda: net(f.commodity), com: net(f.how) })),
    // Els amos del problema: les seves preocupacions són les que defineixen quin és el problema,
    // o sigui que un canvi que en contradigui una no passarà, per bona idea que sigui.
    amos: ((p.analysisOne || {}).issueOwners || []).filter(net),
  };
}

// Si l'eix de la factibilitat té amb què. Es diu, no s'impedeix: es pot fer l'etapa 6 sense les
// Anàlisis, però llavors qui la faci ha de saber que se les està inventant.
export const potJutjarFactibilitat = (problemSituation) => {
  const c = contextCultural(problemSituation);
  return c.papers.length > 0 || c.poder.length > 0;
};

const ANALISIS = { PAPERS_INTERVENCIO, MONEDES, analisisBuides, feta, capFeta,
                   contextCultural, potJutjarFactibilitat };
export default ANALISIS;
